import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { Badge, Button, ChipGroup, DataTable, SearchFilterBar, IdControl, type ColumnDef, type FilterDef } from '@shared-ui/components';
import './AsyncJobs.css';
import { AsyncJobTimings } from './AsyncJobTimings';

/** Summary-section window. Matches the backend's timings windows exactly. */
type SummaryWindow = '1h' | '4h' | '1d' | '7d';
const SUMMARY_WINDOW_OPTIONS: { value: SummaryWindow; label: string }[] = [
  { value: '1h', label: '1 Hour' },
  { value: '4h', label: '4 Hours' },
  { value: '1d', label: '1 Day' },
  { value: '7d', label: '7 Days' },
];
const SUMMARY_WINDOW_MINUTES: Record<SummaryWindow, number> = {
  '1h': 60, '4h': 240, '1d': 1440, '7d': 10080,
};

// Map AsyncJob status → shared Badge variant. Single source of truth used
// by both the per-row status badge and the top-of-page count pills.
const STATUS_BADGE_VARIANT: Record<string, 'info' | 'warning' | 'success' | 'error' | 'default'> = {
  queued:    'info',
  running:   'warning',
  completed: 'success',
  failed:    'error',
  dead:      'default',
};

type AsyncJobItem = NonNullable<Awaited<ReturnType<typeof adminApi.getAsyncJobs>>>['items'][number];
type AsyncJobDetail = NonNullable<Awaited<ReturnType<typeof adminApi.getAsyncJobDetail>>>['job'];

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'dead', label: 'Dead' },
];

const DATE_RANGE_OPTIONS = [
  { value: '1h', label: 'Last 1 hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '3d', label: 'Last 3 days' },
  { value: '7d', label: 'Last 7 days' },
];

function getSinceIso(range: string): string {
  const d = new Date();
  if (range === '1h') d.setHours(d.getHours() - 1);
  else if (range === '24h') d.setHours(d.getHours() - 24);
  else if (range === '3d') d.setDate(d.getDate() - 3);
  else if (range === '7d') d.setDate(d.getDate() - 7);
  return d.toISOString();
}

type AsyncJobsFilters = {
  status: string;
  type: string;
  correlationObjectType: string;
  correlationObjectId: string;
  userEmail: string;
  dateRange: string;
};

export function AsyncJobs() {
  // Filters + offset in the URL so returning from a job detail restores them.
  const [searchParams, setSearchParams] = useSearchParams();
  const appliedFilters = useMemo<AsyncJobsFilters>(() => ({
    status: searchParams.get('status') ?? '',
    type: searchParams.get('type') ?? '',
    correlationObjectType: searchParams.get('correlationObjectType') ?? '',
    correlationObjectId: searchParams.get('correlationObjectId') ?? '',
    userEmail: searchParams.get('userEmail') ?? '',
    dateRange: searchParams.get('dateRange') ?? '24h',
  }), [searchParams]);
  const offset = Number(searchParams.get('offset') ?? '0') || 0;
  // Writes the filters AND resets the page in ONE setSearchParams call. Two
  // separate setSearchParams(prev => …) calls in the same handler would each
  // read the same pre-update `prev` snapshot, so the second clobbers the first
  // (react-router 6): a trailing setOffset(0) would silently drop the filters
  // just written, and the server would be queried unfiltered.
  const setAppliedFilters = (updater: (prev: AsyncJobsFilters) => AsyncJobsFilters) => {
    const next = updater(appliedFilters);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(next)) {
        if (v && !(k === 'dateRange' && v === '24h')) p.set(k, v); else p.delete(k);
      }
      p.delete('offset'); // any filter change returns to the first page
      return p;
    }, { replace: true });
  };
  const setOffset = (val: number) =>
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (val > 0) p.set('offset', String(val)); else p.delete('offset');
      return p;
    }, { replace: true });
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);

  // Type options come from the registered async-job handlers (the single source
  // of truth — same list the tuning page uses), so the dropdown can never drift
  // from what the backend actually runs. Loads on mount, independent of Fetch.
  const { data: jobConfig } = useQuery({
    queryKey: ['async-jobs-config'],
    queryFn: () => adminApi.getAsyncJobConfig(),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['async-jobs-admin', appliedFilters, offset],
    queryFn: () => adminApi.getAsyncJobs({
      status: appliedFilters.status || undefined,
      type: appliedFilters.type || undefined,
      userEmail: appliedFilters.userEmail || undefined,
      correlationObjectType: appliedFilters.correlationObjectType || undefined,
      correlationObjectId: appliedFilters.correlationObjectId || undefined,
      since: getSinceIso(appliedFilters.dateRange),
      limit: PAGE_SIZE,
      offset,
    }),
    enabled: shouldFetch,
    staleTime: Infinity,
    refetchInterval: autoRefresh ? 10_000 : false,
  });

  const typeOptions = useMemo(
    () => (jobConfig?.types ?? []).map(t => ({ value: t.type, label: t.type })),
    [jobConfig],
  );

  // "About"-types have no registry (each enqueue site sets its own), so the
  // canonical source is the data: the distinct correlation types the backend
  // counted in the window. Union in the applied value so a filter restored from
  // the URL is never absent from its own dropdown.
  const correlationTypeOptions = useMemo(() => {
    const seen = new Set(Object.keys(data?.counts.byCorrelationType ?? {}));
    if (appliedFilters.correlationObjectType) seen.add(appliedFilters.correlationObjectType);
    return [...seen].sort().map(v => ({ value: v, label: v }));
  }, [data, appliedFilters.correlationObjectType]);

  const filterDefs = useMemo<FilterDef[]>(() => [
    { key: 'dateRange', kind: 'select', placeholder: 'Date Range', required: true, options: DATE_RANGE_OPTIONS },
    { key: 'status', kind: 'select', placeholder: 'All Statuses', options: STATUS_OPTIONS },
    { key: 'type', kind: 'select', placeholder: 'All Types', options: typeOptions },
    { key: 'correlationObjectType', kind: 'select', placeholder: 'All About-Types', options: correlationTypeOptions },
    { key: 'correlationObjectId', kind: 'text', placeholder: 'About ID (e.g. submission id)' },
    { key: 'userEmail', kind: 'text', placeholder: 'User email' },
  ], [typeOptions, correlationTypeOptions]);

  // One window governs the whole summary section — cards and timings both, so
  // the numbers on screen always describe the same span.
  const [summaryWindow, setSummaryWindow] = useState<SummaryWindow>('1d');
  const windowMinutes = SUMMARY_WINDOW_MINUTES[summaryWindow];

  // Always loads (independent of the filter bar and Fetch button) so admins see
  // system health on tab open.
  const { data: metrics } = useQuery({
    queryKey: ['async-jobs-metrics', windowMinutes],
    queryFn: () => adminApi.getAsyncJobsMetrics(windowMinutes),
    refetchInterval: autoRefresh ? 10_000 : 60_000,
    staleTime: 30_000,
  });

  const handleFiltersChange = (values: Record<string, string>) => {
    // setAppliedFilters already resets offset in the same URL update — do NOT
    // add a separate setOffset(0) here, or it clobbers the filters (see above).
    setAppliedFilters(prev => ({ ...prev, ...(values as Partial<AsyncJobsFilters>) }));
    setShouldFetch(true);
  };

  const totalPages = data
    ? (data.hasMore ? Math.floor(offset / PAGE_SIZE) + 2 : Math.floor(offset / PAGE_SIZE) + 1)
    : 0;

  const handleRetry = async (id: string) => {
    if (!confirm(`Retry async job ${id}? It must be in 'failed' status.`)) return;
    try {
      await adminApi.retryAsyncJob(id);
      await refetch();
    } catch (err) {
      alert(`Retry failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleMarkDead = async (id: string) => {
    if (!confirm(`Mark async job ${id} as dead? This abandons the work permanently.`)) return;
    try {
      await adminApi.markAsyncJobDead(id);
      await refetch();
    } catch (err) {
      alert(`Mark dead failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const columns = useMemo<ColumnDef<AsyncJobItem>[]>(() => [
    { key: 'time', header: 'Queued', className: 'aj-num', render: (j) => format(new Date(j.queuedAt), 'MMM d HH:mm:ss') },
    { key: 'type', header: 'Type', render: (j) => <Badge variant="default" size="sm">{j.type}</Badge> },
    { key: 'status', header: 'Status', render: (j) => (
        <Badge variant={STATUS_BADGE_VARIANT[j.status] ?? 'default'} size="sm">
          {j.status}
        </Badge>
    ) },
    { key: 'attempts', header: 'Attempts', className: 'aj-num', render: (j) => `${j.attempts}/${j.maxAttempts}` },
    { key: 'wait', header: 'Wait', className: 'aj-num', render: (j) => {
        if (!j.startedAt) return '—';
        const waitMs = new Date(j.startedAt).getTime() - new Date(j.queuedAt).getTime();
        return `${(waitMs / 1000).toFixed(1)}s`;
    } },
    { key: 'latency', header: 'Latency', className: 'aj-num', render: (j) => j.latencyMs !== undefined ? `${(j.latencyMs / 1000).toFixed(1)}s` : '—' },
    // The list handler batch-resolves the owner per page, so show who it is
    // rather than an opaque id. The id stays available via IdControl for the
    // cases that need it (copy, cross-reference) and as the fallback when the
    // user no longer exists.
    { key: 'user', header: 'User', render: (j) => (
        <span onClick={e => e.stopPropagation()} className="aj-user">
          {j.teacherName || j.teacherEmail
            ? (
              <span className="aj-user-known" title={[j.teacherEmail, j.userId].filter(Boolean).join(' · ')}>
                <span className="aj-user-name">{j.teacherName || j.teacherEmail}</span>
                {j.teacherName && j.teacherEmail && <span className="aj-user-email">{j.teacherEmail}</span>}
              </span>
            )
            : <IdControl value={j.userId} container="users" />}
        </span>
    ) },
    { key: 'correlation', header: 'About', className: 'aj-mono', render: (j) => (
        j.correlationObjectType && j.correlationObjectId
          ? `${j.correlationObjectType}/${j.correlationObjectId.slice(0, 8)}`
          : '—'
    ) },
    { key: 'claimedBy', header: 'Claimed by', className: 'aj-mono aj-muted', render: (j) => j.claimedBy ?? '—' },
    { key: 'error', header: 'Last error', className: 'aj-error-cell', render: (j) =>
        j.lastError ? <span title={j.lastError.message}>{j.lastError.message.slice(0, 60)}{j.lastError.message.length > 60 ? '…' : ''}</span> : ''
    },
  ], []);

  return (
    <div className="settings-section async-jobs-section">
      <div className="section-header aj-section-header">
        <h2>Async Jobs</h2>
        <label className="aj-autorefresh">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh (10s)
        </label>
      </div>

      <ChipGroup
        value={summaryWindow}
        options={SUMMARY_WINDOW_OPTIONS}
        onChange={setSummaryWindow}
        ariaLabel="Summary window"
      />

      <div className="stat-cards stat-cards--5col aj-metric-cards">
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{metrics?.succeeded ?? '—'}</div>
          <div className="stat-card-label">Succeeded</div>
        </div>
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{metrics?.failed ?? '—'}</div>
          <div className="stat-card-label">Failed</div>
        </div>
        {/* Not window-scoped — a queue depth is only meaningful right now. */}
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{metrics?.queuedNow ?? '—'}</div>
          <div className="stat-card-label">Waiting to be picked (now)</div>
        </div>
        {/* Read with the card beside it: a backlog with nothing running is a
            stuck dispatcher, which a queue depth alone doesn't distinguish. */}
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{metrics?.runningNow ?? '—'}</div>
          <div className="stat-card-label">Running (now)</div>
        </div>
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">
            {metrics?.avgWaitSeconds != null
              ? `${metrics.avgWaitSeconds.toFixed(1)}s`
              : '—'}
          </div>
          <div className="stat-card-label">Avg wait time</div>
        </div>
      </div>

      {/* Percentiles behind the averages above — pickup vs execution, so a slow
          job is attributable to the queue or to the work. */}
      <AsyncJobTimings window={summaryWindow} autoRefresh={autoRefresh} />

      {data?.counts && (
        <div className="aj-counts-row">
          {Object.entries(data.counts.byStatus).map(([status, count]) => (
            <Badge
              key={status}
              variant={STATUS_BADGE_VARIANT[status] ?? 'default'}
              size="md"
            >
              {status} {count}
            </Badge>
          ))}
        </div>
      )}

      <SearchFilterBar
        filters={filterDefs}
        values={appliedFilters as unknown as Record<string, string>}
        onChange={handleFiltersChange}
        onFetch={() => refetch()}
        loading={isLoading}
        hideSearch
      />

      {data && (
        <DataTable<AsyncJobItem>
          columns={columns}
          data={data.items}
          rowKey={(j) => j.id}
          renderExpandedRow={(j) => <AsyncJobDetailRow id={j.id} job={j} onRetry={handleRetry} onMarkDead={handleMarkDead} />}
          pagination={(data.hasMore || offset > 0) ? {
            page: Math.floor(offset / PAGE_SIZE) + 1,
            totalPages,
            onPageChange: (p) => setOffset((p - 1) * PAGE_SIZE),
          } : undefined}
          compact
          bordered
          emptyMessage="No async jobs found for the selected filters."
        />
      )}

      {!shouldFetch && (
        <div className="no-logs-message">
          Click "Fetch" to load async jobs from CosmosDB.
        </div>
      )}
    </div>
  );
}

interface AsyncJobDetailRowProps {
  id: string;
  job: AsyncJobItem;
  onRetry: (id: string) => void | Promise<void>;
  onMarkDead: (id: string) => void | Promise<void>;
}

function AsyncJobDetailRow({ id, job, onRetry, onMarkDead }: AsyncJobDetailRowProps) {
  // Lazy-load full payload + result when the row is expanded.
  const { data: detail, isLoading } = useQuery({
    queryKey: ['async-job-detail', id],
    queryFn: () => adminApi.getAsyncJobDetail(id),
    staleTime: 30_000,
  });

  const fullJob: AsyncJobDetail | undefined = detail?.job;

  return (
    <div className="aj-details">
      <div className="aj-detail-actions">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigator.clipboard.writeText(id).then(() => undefined)}
        >
          Copy ID
        </Button>
        {job.status === 'failed' && (
          <Button variant="outline" size="sm" onClick={() => onRetry(id)}>
            Retry
          </Button>
        )}
        {(job.status === 'failed' || job.status === 'running' || job.status === 'queued') && (
          <Button variant="danger" size="sm" onClick={() => onMarkDead(id)}>
            Mark dead
          </Button>
        )}
      </div>

      <div className="aj-detail-grid">
        <div className="aj-detail-item"><strong>ID:</strong> {id}</div>
        <div className="aj-detail-item"><strong>Type:</strong> {job.type}</div>
        <div className="aj-detail-item"><strong>Status:</strong> {job.status}</div>
        <div className="aj-detail-item"><strong>Attempts:</strong> {job.attempts} of {job.maxAttempts}</div>
        <div className="aj-detail-item">
          <strong>User:</strong>{' '}
          {job.teacherName || job.teacherEmail
            ? <>{job.teacherName || job.teacherEmail}{job.teacherEmail && job.teacherName ? ` · ${job.teacherEmail}` : ''} <span className="aj-muted">({job.userId})</span></>
            : job.userId}
        </div>
        <div className="aj-detail-item"><strong>Queued:</strong> {format(new Date(job.queuedAt), 'PPpp')}</div>
        {job.startedAt && <div className="aj-detail-item"><strong>Started:</strong> {format(new Date(job.startedAt), 'PPpp')}</div>}
        {job.completedAt && <div className="aj-detail-item"><strong>Completed:</strong> {format(new Date(job.completedAt), 'PPpp')}</div>}
        {job.nextAttemptAt && <div className="aj-detail-item"><strong>Next attempt:</strong> {format(new Date(job.nextAttemptAt), 'PPpp')}</div>}
        {job.claimedBy && <div className="aj-detail-item"><strong>Claimed by:</strong> {job.claimedBy}</div>}
        {job.dedupKey && <div className="aj-detail-item"><strong>Dedup key:</strong> {job.dedupKey}</div>}
        {job.correlationObjectType && (
          <div className="aj-detail-item">
            <strong>Correlation:</strong> {job.correlationObjectType} / {job.correlationObjectId}
          </div>
        )}
        {job.metadata && Object.keys(job.metadata).length > 0 && (
          <div className="aj-detail-item">
            <strong>Metadata:</strong>
            <pre>{JSON.stringify(job.metadata, null, 2)}</pre>
          </div>
        )}
        {job.lastError && (
          <div className="aj-detail-item aj-error-detail">
            <strong>Last error:</strong>
            <pre>{job.lastError.message}{job.lastError.type ? ` (${job.lastError.type})` : ''}</pre>
            <small>at {format(new Date(job.lastError.attemptedAt), 'PPpp')}</small>
          </div>
        )}
      </div>

      {isLoading && <div className="aj-loading">Loading payload + result…</div>}

      {fullJob && (
        <>
          <details className="aj-collapsible">
            <summary><strong>Payload</strong></summary>
            <pre className="aj-json">{JSON.stringify(fullJob.payload, null, 2)}</pre>
          </details>
          {fullJob.result !== undefined && fullJob.result !== null && (
            <details className="aj-collapsible">
              <summary><strong>Result</strong></summary>
              <pre className="aj-json">{JSON.stringify(fullJob.result, null, 2)}</pre>
            </details>
          )}
        </>
      )}
    </div>
  );
}
